import React, { useState } from 'react';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { Loader2 } from 'lucide-react';
import { Button } from '@cargomatic/ui';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import DateTimeField from 'components/v2/DateTimeField';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import useAutoUnitCalculation from 'components/v2/hooks/useAutoUnitCalculation';
import { getRequestFields } from './accessorialRequestPaneUtils';
import { getLabelInfo, locationTypes } from './editFormHelpers';

const EditAccessorialForm = ({
  referenceAccessorial,
  accessorial,
  isCarrier,
  isSaving,
  onChange,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [label, info] = getLabelInfo(referenceAccessorial);
  const [errors, setErrors] = useState({
    carrier_rate: null,
    shipper_rate: null,
    carrier_unit_qty: null,
    shipper_unit_qty: null,
  });
  const {
    rateField,
    unitsField,
    beginDateField,
    endDateField,
    locationField,
    descriptionField,
    notesField,
    rate,
    units,
    beginDate,
    endDate,
    location,
    description,
    notes,
  } = getRequestFields(accessorial, isCarrier);

  function handleErrors(field, value) {
    setErrors((prev) => {
      let previous = { ...prev };
      // Payee rates can be negative
      if (field === 'carrier_rate') {
        previous[field] = null;
      } else if (field === 'shipper_rate') {
        let parsedValue = parseFloat(value);
        previous[field] =
          isNil(parsedValue) || parsedValue < 0
            ? t('VALUE_MUST_BE_A_NUMBER_AND_CANNOT_BE_NEGATIVE')
            : null;
      } else {
        let parsedValue = parseFloat(value);
        previous[field] =
          !parsedValue || parsedValue < 0
            ? t('VALUE_MUST_BE_A_NUMBER_AND_CANNOT_BE_NEGATIVE')
            : null;
      }
      return previous;
    });
  }

  function handleChange(field) {
    return function (value) {
      const locationValueFields = ['shipper_location', 'location'];
      const positiveValueFields = [
        'carrier_rate',
        'shipper_rate',
        'carrier_unit_qty',
        'shipper_unit_qty',
      ];

      if (positiveValueFields.includes(field)) {
        handleErrors(field, value);
        onChange(field, value);
      } else if (locationValueFields.includes(field)) {
        onChange(field, value.value);
      } else {
        onChange(field, value);
      }
    };
  }

  function handleSave() {
    if (dateError || isSaving) return;
    let errorsValidated = true;
    const errorsToCheck = Object.keys(errors);

    for (let i = 0; i < errorsToCheck.length; i++) {
      const item = errorsToCheck[i];
      if (errors[item]) {
        errorsValidated = false;
        break;
      }
    }
    if (errorsValidated) onSave();
  }

  // Auto-calculate units: when the accessorial is configured for it and this
  // side is covered by Applies To, the units field is system-managed (read-only)
  // and recalculated from Begin/End dates. Other sides stay manually editable.
  const { managed: unitsAutoManaged, error: dateError } =
    useAutoUnitCalculation({
      accessorial: referenceAccessorial,
      beginDate,
      endDate,
      side: isCarrier ? 'carrier' : 'shipper',
      currentUnits: units,
      onUnitsChange: (value) => onChange(unitsField, value),
    });

  const showBeginDate = referenceAccessorial.begin_date_required || beginDate;
  const showEndDate = referenceAccessorial.end_date_required || endDate;
  const showLocation = referenceAccessorial.location_required || location;
  const showDescription =
    referenceAccessorial.description_required || description;

  return (
    <div data-testid="div-7da91118" className="p-4 bg-gray-50">
      <div data-testid="div-1d3de2e3" className="flex mb-4 gap-4">
        <div data-testid="div-b0b4eef1" className="flex-1 mb-4">
          <CargomaticInput
            dataTestId="CargomaticInput-a3020910"
            value={units}
            type="number"
            label={t(label)}
            info={t(info)}
            placeholder={t('UNITS_PLACEHOLDER')}
            onChange={handleChange(unitsField)}
            maxLength={35}
            disabled={unitsAutoManaged}
          />
          {unitsAutoManaged ? (
            <p className="mt-1 text-xs text-gray-500">
              {t(
                'AUTO_CALCULATED_FROM_DATES',
                'Auto-calculated from Begin and End Date.',
              )}
            </p>
          ) : (
            <ErrorLabel dataTestId="ErrorLabel-ea22ccb6" className="mt-1">
              {errors[unitsField]}
            </ErrorLabel>
          )}
        </div>
        <div data-testid="div-e4f53b07" className="flex-1 mb-4">
          <CargomaticInput
            dataTestId="CargomaticInput-701ee0c0"
            value={rate}
            type="number"
            label={t('RATE_PER_UNIT')}
            info={t('ACCESSORIAL_RATE')}
            placeholder={t('RATE', { count: 1 })}
            onChange={handleChange(rateField)}
            size="20"
          />
          <ErrorLabel dataTestId="ErrorLabel-9305067f" className="mt-1">
            {errors[rateField]}
          </ErrorLabel>
        </div>
      </div>
      <div data-testid="div-103a7e17" className="flex flex-col -mx-2">
        {showBeginDate && (
          <div data-testid="div-824ddee0" className="px-2 mb-4">
            <label
              data-testid="label-c692eeb5"
              htmlFor="beginDate"
              className="block mb-1 form-label"
            >
              {t('BEGIN_DATE')}
            </label>
            <DateTimeField
              className="!flex-nowrap [&>*]:flex-1"
              dateTestId="DateTimeField-begin-date"
              timeTestId="DateTimeField-begin-time"
              value={beginDate || null}
              onChange={handleChange(beginDateField)}
              timeIntervals={5}
            />
          </div>
        )}
        {showEndDate && (
          <div data-testid="div-80400f2a" className="px-2 mb-4">
            <label
              data-testid="label-36458e54"
              htmlFor="endDate"
              className="block mb-1 form-label"
            >
              {t('END_DATE')}
            </label>
            <DateTimeField
              className="!flex-nowrap [&>*]:flex-1"
              dateTestId="DateTimeField-end-date"
              timeTestId="DateTimeField-end-time"
              value={endDate || null}
              onChange={handleChange(endDateField)}
              timeIntervals={5}
            />
            {dateError && (
              <ErrorLabel id="end-before-begin" className="mt-1">
                {t(dateError, 'End Date cannot be before Begin Date.')}
              </ErrorLabel>
            )}
          </div>
        )}
        {showLocation && (
          <div data-testid="div-5af39733" className="px-2 mb-4">
            <CargomaticSelect
              dataTestId="CargomaticSelect-a9729c71"
              selected={location}
              fullWidth={false}
              items={locationTypes}
              defaultSelectLabel=""
              label={t('LOCATION')}
              info={t('LOCATION_CUSTOMER_OR_TERMINAL')}
              onSelect={handleChange(locationField)}
              showCaption={false}
              valueKey="value"
              textKey="localizationSlug"
            />
          </div>
        )}
      </div>
      {showDescription && (
        <div data-testid="div-2fba806f" className="flex -mx-2">
          <div data-testid="div-8c413d30" className="w-full px-2 mb-4">
            <CargomaticInput
              dataTestId="CargomaticInput-a9b1f3b2"
              value={description}
              type="text"
              label={t('DESCRIPTION', { count: 1 })}
              placeholder={t('DESCRIPTION', { count: 1 })}
              onChange={handleChange(descriptionField)}
            />
          </div>
        </div>
      )}
      <div data-testid="div-3ea69e7d" className="mb-4">
        <CargomaticInput
          dataTestId="CargomaticInput-a8d3aebf"
          value={notes}
          label={titleize(t('NOTE', { count: 3 }))}
          info={titleize(t('NOTE', { count: 3 }))}
          type="textarea"
          placeholder={titleize(t('NOTE', { count: 3 }))}
          onChange={handleChange(notesField)}
        />
      </div>
      <div
        data-testid="div-55bd5128"
        className="flex items-center justify-end gap-2 mt-6"
      >
        <Button
          data-testid="button-70262908"
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="bg-green-cm hover:bg-green-700 text-white"
        >
          {isSaving ? (
            <span className="flex items-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {titleize(t('SAVING'))}...
            </span>
          ) : (
            titleize(t('SAVE'))
          )}
        </Button>
        <Button
          data-testid="button-0d6cf1a1"
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving}
        >
          {t('CANCEL')}
        </Button>
      </div>
    </div>
  );
};

export default EditAccessorialForm;
